import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import TWEEN, { Easing, Tween } from "@tweenjs/tween.js"
import { faArrowsToCircle, faMagnifyingGlassMinus, faMagnifyingGlassPlus, faUserAstronaut } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GameInstance, State } from "../../types";
import { getCharacterCoords, getCharacterModelCoords, moveCharacterToNodeInSubMap, renderNodeItems, renderSubMapCharacters } from "./helpers/helpers";
import * as helpers from "./helpers/helpers";
import * as lib3d from "./Lib3d/index";
import { baseURI, baseURL, socket } from "../../api";
import { actions, closeWindow, openWindow } from "../../actions";
import Inventory from "./subcomponents/Inventory";
import { CharacterId } from "../../types/GameTypes";
import Combat from "./subcomponents/Combat";
import Actions from "./subcomponents/Actions";
import Calendar from "./subcomponents/Calendar";
import CharThumbnail from "./subcomponents/CharThumbnail";
import CharacterStats from "./subcomponents/CharacterStats";
import Dialogue from "./subcomponents/Dialogue";
import { getConditionIcon } from "./helpers/uiHelpers";
import Info from "./subcomponents/Info";
import { getActionCount } from "./helpers/dataHelpers";

export const durations = {
    default: 300,
    openSubMap: 300,
    cameraMovement: 500,
    initialZoom: 500,
}

export const mdlHeights = {
    ground: 0,
    paths: 0.1,
    nodes: 0.2,
    nodes_items: 0.2,
    nodes_title: 0.1,
    characters: 0.3,
    submaps: 0.4,
    defaultCamera: 75,
    cameraClose: 35,
}

const zoomLevels = [22.5, 35, 65, 95, 120]

const raycaster = new THREE.Raycaster()
const mouse = new THREE.Vector2()
let intersects = []
let cameraAni: Tween | null = null;

export const moveCameraToNode = (nodeId: string, dur: number, subInstanceId?: string) => {
    const pos = helpers.getNodeModelCoords(nodeId, subInstanceId)
    cameraAni = new TWEEN.Tween(camera.position).to({ x: pos.x, y: camera.position.y, z: pos.z }, dur)
            .easing(Easing.Quadratic.InOut)
            .start()
}
export type GameAnimation = { id: string, ani: Tween | null}
export let animations: GameAnimation[] = [];
export const cleanAnimations = () => {
    animations = animations.filter(a => a?.ani?.isPlaying())
    // console.log("ani", animations.length)
}
export const removedAnimatedIds:string[] = []

// let canvasRef = React.createRef();
let canvasWidth: number = window.innerWidth;
let canvasHeight: number = Math.max(document.documentElement.clientHeight, window.innerHeight);
// let renderer: THREE.WebGLRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
let renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });

// renderer.toneMapping = THREE.NoToneMapping;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
// renderer.shadowMap.enabled = false;
renderer.outputColorSpace = THREE.SRGBColorSpace;

export let camera = lib3d.camera();
export let scene = new THREE.Scene();
export let expGameInstance: GameInstance | null = null

// renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.LinearToneMapping;
renderer.toneMappingExposure = 1;

let followCharacterId: string | null = null

renderer.setSize(canvasWidth, canvasHeight);
renderer.setPixelRatio(window.devicePixelRatio)

let lastRender = 0;
let lastNodeChecksum = 0

type Props = {
    time: number;
}
const ThreeTestFC: React.FC<Props> = (props) => {

    const gameInstance = useSelector((store: State) => store.gameInstance)
    expGameInstance = gameInstance
    const currentCharacterId = useSelector((store: State) => store.currentCharacterId)
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const myCharacter = gameInstance?.characters.find(c => c.id === currentCharacterId)
    const openWindows = useSelector((store: State) => store.applicationStatus.openWindows)
    const nodes = useSelector((store: State) => store.gameInstance?.map.nodes)

    const [ info, setInfo ] = useState<string[]>([])

    useEffect(() => {
        if (!gameInstance) {
            navigate(`${baseURI}`)
        }
    });

    // TODO: Better way to detect updated nodes
    useEffect(() => {
        if (!nodes) { return }
        let checksum = JSON.stringify(nodes).length
        if (lastNodeChecksum !== checksum) {
            console.log("Re-render all node items")
            if (lastNodeChecksum !== 0) {
                for (const n of nodes ?? []) {
                    renderNodeItems(n.id)
                }
            }
            lastNodeChecksum = checksum
        }
    }, [nodes]);

    useEffect(() => {
        if (myCharacter?.dialogueStatus.dialogueActive) {
            if (!openWindows.includes("interaction")) {
                dispatch(openWindow("interaction"))
            }
        } else {
            if (openWindows.includes("interaction")) {
                dispatch(closeWindow("interaction"))
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myCharacter?.dialogueStatus]);

    useEffect(() => {
        // console.log("Dialogue status changed", myCharacter?.dialogueStatus)
        if (myCharacter?.combatStatus.combatActive) {
            if (!openWindows.includes("combat")) {
                dispatch(openWindow("combat"))
            }
        } else {
            if (openWindows.includes("combat")) {
                dispatch(closeWindow("combat"))
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myCharacter?.combatStatus]);

    useEffect(() => {
        if (lastRender !== props.time) {
            render3d()
        }
        lastRender = props.time
        return () => {
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.time]);

    useEffect(() => {
        socket.on("cMovedToNode", (d: {characterId: string, nodeId: string, subInstanceId?: string}) => {
            dispatch(actions.updateCharacterLocation(
                {
                    gameInstanceId: gameInstance?.id ?? "",
                    characterId: d.characterId,
                    nodeId: d.nodeId,
                    subInstanceId: d.subInstanceId,
                }
            ))
            setTimeout(() => {
                // Refresh Move buttons
                const characterGroup = scene.getObjectByName(`char_${d.characterId}`)
                const btns = characterGroup?.getObjectsByProperty("name", "moveBtns")
                for (const b of btns ?? []) {
                    characterGroup?.remove(b)
                }
                const char = expGameInstance?.characters.find(c => c.id === d.characterId)
                char && characterGroup?.add(lib3d.getMoveBtns(char))
            }, 100);
        });

        socket.on("cMovingToNode", (d: {characterId: string, nodeId: string, duration: number, subInstanceId?: string}) => {
            if (d.subInstanceId) {
                moveCharacterToNodeInSubMap(scene, animations, d.characterId, d.subInstanceId, d.nodeId, d.duration)
            } else {
                helpers.moveCharacterToNode(scene, animations, d.characterId, d.nodeId, d.duration)
            }
            if (followCharacterId === d.characterId) {
                moveCameraToNode(d.nodeId, d.duration, d.subInstanceId)
            }
        });

        socket.on("cMovedToSubMap", (d: {characterId: string, subId: string, nodeId: string}) => {
            helpers.displaySubMap(d.subId)

            dispatch(actions.updateCharacterLocation(
                {
                    gameInstanceId: gameInstance?.id ?? "",
                    characterId: d.characterId,
                    nodeId: d.nodeId,
                    subInstanceId: d.subId,
                }
            ))

            if (followCharacterId === d.characterId) {
                setTimeout(() => {
                    // TODO: Wait twice only if moving from one submap to another (longer animation)
                    moveCameraToNode(d.nodeId, durations.openSubMap + durations.cameraMovement, d.subId)
                }, durations.openSubMap * 2)
            }
        });

        socket.on("cMovedToBaseMap", (d: {characterId: string, fromSubId: string, nodeId: string}) => {
            helpers.moveCharacterToNode(scene, animations, d.characterId, d.nodeId, 100)

            if (followCharacterId === d.characterId) {
                setTimeout(() => {
                    moveCameraToNode(d.nodeId, durations.openSubMap + durations.cameraMovement)
                }, durations.openSubMap + 100)
            }
            helpers.hideSubMap(d.fromSubId)
            dispatch(actions.updateCharacterLocation(
                {
                    gameInstanceId: gameInstance!.id,
                    characterId: d.characterId,
                    nodeId: d.nodeId,
                    subInstanceId: undefined,
                }
            ))
        });

        socket.on("updateGameInstance", (d: GameInstance) => {
            expGameInstance = d  // To avoid delay associated with state
            dispatch(actions.setGameInstanceAction(d))
        })

        socket.on("renderNode", (d: { nodeId: string, subInstanceId?: string }) => {
            helpers.renderNodeItems(d.nodeId, d.subInstanceId)
        })

        socket.on("renderCharacter", (d: { characterId: string }) => {
            const char = gameInstance?.characters.find(c => c.id === d.characterId)
            if (!char) return
            if (!gameInstance) return
            helpers.reRenderCharacter(char, gameInstance)
        })

        socket.on("renderNodes", (d: { nodeId: string, subInstanceId?: string }[]) => {
            for (const n of d) {
                helpers.renderNodeItems(n.nodeId, n.subInstanceId)
            }
        })

        socket.on("itemDropped", (d: { nodeId: string, subInstanceId: string}) => {
            helpers.renderNodeItems(d.nodeId, d.subInstanceId)
        })

        socket.on("itemPickedUp", (d: { nodeId: string, subInstanceId: string}) => {
            helpers.renderNodeItems(d.nodeId, d.subInstanceId)
        })

        socket.on("combatEnded", (d: { nodeId: string, subInstanceId: string}) => {
            helpers.renderNodeItems(d.nodeId, d.subInstanceId)
        })

        socket.on("messages", (d: { type: string, message: string}[]) => {
            console.log("Messages received", d)
        })

        socket.on("log", (d: { message: string }) => {
            console.log(">", d.message)
        })

        socket.on("info", (d: { message: string }) => {
            console.log("Info:", d)
            setInfo([...info, d.message])
        })


        return () => {
            socket.off("cMovingToNode");
            socket.off("cMovedToNode");
            socket.off("cMovedToSubMap");
            socket.off("cMovedToBaseMap");
            socket.off("updateGameInstance");
            socket.off("itemDropped");
            socket.off("itemPickedUp");
            socket.off("messages");
            socket.off("combatEnded");
            socket.off("renderNode");
            socket.off("renderNodes");
            socket.off("renderCharacter");
            socket.off("log");
            socket.off("info");
        };
    });

    const render3d = async () => {
        if (!gameInstance) { return }
        document.getElementById("three-canvas")?.appendChild(renderer.domElement);
        scene.clear()
        camera = lib3d.camera()

        setTimeout(() => { zoomInToCurrentCharacter() }, durations.initialZoom)

        let baseMap = gameInstance.map
        if (!baseMap) { console.error("no map"); return; }

        // scene.add(lib3d.pos(0, 30, 0, lib3d.pointLight()));
        // scene.add(new THREE.AmbientLight(0xFFFDE7, 5))
        scene.add(new THREE.AmbientLight(0xffffff, 4))

        const baseMapGroup = new THREE.Group()
        baseMapGroup.name = "baseMap"
        baseMapGroup.add(lib3d.pos(0, mdlHeights.ground, 0, await lib3d.ground(baseMap?.img ?? "")));

        // Nodes
        const nodes = baseMap?.nodes ?? []
        for (const n of gameInstance.map.nodes) {
            const nodeGroup = lib3d.node(n, gameInstance.map.characterScale ?? 4)
            baseMapGroup.add(lib3d.pos(n.x, mdlHeights.nodes, n.y, nodeGroup));
            renderNodeItems(n.id, undefined, nodeGroup)
        }
        baseMapGroup.add(lib3d.getNodeConnections(nodes))

        // Characters
        for (const c of gameInstance.characters) {
            const {x, y} = getCharacterCoords(c, gameInstance)
            baseMapGroup.add(lib3d.pos(x, mdlHeights.characters, y, lib3d.character(c, gameInstance.map.characterScale ?? 5)));
        }
        scene.add(baseMapGroup)

        // SubInstances
        for (const sub of gameInstance.subInstances) {
            scene.add(lib3d.createSubmap(sub));
            renderSubMapCharacters(sub)
        }

        renderer.setAnimationLoop(animation);
    }

    const zoomOut = () => {
        const zIx = zoomLevels.findIndex(l => l === camera.position.y) ?? 2
        if (zIx >= zoomLevels.length - 1) { return }
        cameraAni = new TWEEN.Tween(camera.position).to({ x: camera.position.x, y: zoomLevels[zIx + 1], z: camera.position.z }, durations.cameraMovement)
            .easing(Easing.Quadratic.InOut)
            .start()
            .onComplete(() => {
                // followCharacterId = null
                console.log(camera.position.y)
            })
    }
    const zoomIn = () => {
        const zIx = zoomLevels.findIndex(l => l === camera.position.y) ?? 2
        if (zIx < 1) { return }
        cameraAni = new TWEEN.Tween(camera.position).to({ x: camera.position.x, y: zoomLevels[zIx - 1], z: camera.position.z }, durations.cameraMovement)
            .easing(Easing.Quadratic.InOut)
            .start()
            .onComplete(() => {
                // followCharacterId = null
                console.log(camera.position.y)
            })
    }

    const moveCameraOnCharacter = (cId: CharacterId) => {
        const {x, z} = getCharacterModelCoords(scene, gameInstance!, cId)
        cameraAni = new TWEEN.Tween(camera.position).to({ x: x, y: camera.position.y, z: z }, durations.cameraMovement)
            .easing(Easing.Quadratic.InOut)
            .start()
            .onComplete(() => {
                followCharacterId = cId
            })
    }

    const moveCameraOnCenter = () => {
        cameraAni = new TWEEN.Tween(camera.position).to({ x: 0, y: camera.position.y, z: 0 }, durations.cameraMovement)
            .easing(Easing.Quadratic.InOut)
            .start()
            .onComplete(() => {
                followCharacterId = null
            })
    }

    const zoomInToCurrentCharacter = () => {
        const {x, z} = getCharacterModelCoords(scene, gameInstance!, gameInstance!.characters[0].id)
        cameraAni = new TWEEN.Tween(camera.position).to({ x: x, y: zoomLevels[1], z: z }, durations.cameraMovement)
            .easing(Easing.Quadratic.InOut)
            .start()
            .onComplete(() => {
                // Set to follow my character
                followCharacterId = gameInstance!.characters[0].id
            })
    }

    const openInventory = () => {
        dispatch(openWindow("inventory"))
    }

    const openActiveMystery = () => {
        socket.emit("openActiveMystery")
    }

    const mouseClicked = (e: React.MouseEvent) => {
        mouse.set((e.clientX / window.innerWidth) * 2 - 1, -(e.clientY / window.innerHeight) * 2 + 1)
        raycaster.setFromCamera(mouse, camera)
        const subInstanceId = gameInstance?.characterLocations.find(c => c.charId === myCharacter?.id)?.subInstanceId
        const children = subInstanceId
            ? scene.getObjectByName(`submap_${subInstanceId}`)?.children
            : scene.getObjectByName("baseMap")?.children
        if (!children) { return }
        intersects = raycaster.intersectObjects(children, true)

        const clicks: Record<string, string | null> = {
            npc: null,
            node: null,
            moveBtn: null,
            char: null,
            gate: null,
            clue: null,
            item: null,
        }
        for (const i of intersects) {
            if (i.object.userData.type === "item") {
                if (clicks.item === null) {
                    clicks.item = i.object.userData.id
                }
            }
            if (i.object.userData.type === "gate") {
                if (clicks.gate === null) {
                    clicks.gate = i.object.userData.id
                }
            }
            if (i.object.userData.type === "clue") {
                if (clicks.clue === null) {
                    clicks.clue = i.object.userData.id
                }
            }
            if (i.object.userData.type === "npc-mesh") {
                if (clicks.npc === null) {
                    clicks.npc = i.object.userData.id
                }
            }
            if (i.object.userData.type === "char") {
                if (clicks.char === null) {
                    clicks.char = i.object.userData.characterId
                }
            }
            if (i.object.userData.type === "node") {
                console.log("node clicked", i.object.userData.id)
                if (clicks.node === null) {
                    clicks.node = i.object.userData.id
                }
            }
            if (i.object.userData.type === "moveBtn") {
                console.log("moveBtn clicked", i.object.userData)
                if (clicks.moveBtn === null) {
                    clicks.moveBtn = i.object.userData.targetNodeId
                }
            }
            if (i.object.name === "baseMap-mesh") {
                console.log("Base Map clicked", Math.round(i.point.x), Math.round(i.point.z))
            }
        }

        // Resolve clicks
        if (clicks.moveBtn) {
            socket.emit("moveToNode", { nodeId: clicks.moveBtn })
            return
        }
        if (clicks.npc) {
            socket.emit("intWithNpc", {npcId: clicks.npc})
            return
        }
        if (clicks.item) {
            socket.emit("pickUpItem", clicks.item)
            return
        }
        if (clicks.gate) {
            socket.emit("encounterGate", { logicBlockId: (clicks.gate) })
            return
        }
        if (clicks.clue) {
            socket.emit("encounterClue", { logicBlockId: (clicks.clue) })
            return
        }
        if (clicks.node) {
            socket.emit("moveToNode", { nodeId: clicks.node })
            return
        }

    }

    const animation = (time: number) => {
        renderer.render(scene, camera);
        cameraAni?.update(time);
        animations.forEach((a) => {
            if (a !== null) {
                a.ani?.update(time)
            }
        })
    }

    return (
        gameInstance && <>
            <div id="three-canvas" style={{ width: "100%", height: "100%" }} onClick={mouseClicked}>

                <div className="toolbar left">
                    <div className="button" onClick={zoomIn} style={{backgroundImage: "url(img/ui/sq_btn_01.png)"}}>
                        <FontAwesomeIcon icon={faMagnifyingGlassPlus} size="xl" />
                    </div>
                    <div className="button" onClick={zoomOut} style={{backgroundImage: "url(img/ui/sq_btn_01.png)"}}>
                        <FontAwesomeIcon icon={faMagnifyingGlassMinus} size="xl" />
                    </div>
                    {
                        gameInstance.characters.map((c) => (
                            <div key={c.id} className="button" onClick={() => { moveCameraOnCharacter(c.id) }} style={{backgroundImage: "url(img/ui/sq_btn_01.png)"}}>
                                <FontAwesomeIcon icon={faUserAstronaut} size="xl" />
                            </div>
                        ))
                    }
                    <div className="button" onClick={moveCameraOnCenter} style={{backgroundImage: "url(img/ui/sq_btn_01.png)"}}>
                        <FontAwesomeIcon icon={faArrowsToCircle} size="xl" />
                    </div>
                </div>

                <div className="toolbar bottom">
                    {((myCharacter?.conditions.length ?? 0) > 0) && <div className="condition-icons" style={{ position: "absolute", left: "5px", top: "-15px", textAlign: "left"}}>
                        {
                            myCharacter?.conditions.map(c => <>
                                <span key={c.name} className="me-1">{getConditionIcon(c.name)}</span>
                            </>)
                        }
                    </div> }
                    <div className="clickable" onClick={() => { dispatch(openWindow("characterStats")) } } style={{ width: "80px", margin: "10px auto"}}>
                        {myCharacter && <CharThumbnail character={myCharacter} size={80} displayHS />}
                    </div>
                </div>
            </div>

            <div className="toolbar right">

                <Calendar minutes={gameInstance.time} />

                <div
                    title="Solve mysteries before Doom drops to zero"
                    className="icon"
                    style={{backgroundImage: "url(img/ui/sq_btn_01.png)"}}
                >
                    <span className="doom">{gameInstance.doom ?? 0}</span>
                    <span className="title">Doom</span>
                </div>

            </div>

            <div className="toolbar right-bottom" style={{ bottom: "10px", right: "10px"}}>

                <div className="icon clickable" style={{backgroundImage: "url(img/ui/sq_btn_01.png)"}} onClick={openActiveMystery}>
                    <span className="mystery">?</span>
                    <span className="title">Mystery</span>
                </div>

                <div className="button" onClick={ ()=>{ dispatch(openWindow("actions")) } } style={{backgroundImage: "url(img/ui/sq_btn_01.png)"}}>
                    <img src={`${baseURL}/img/assets/ui/icon_detective.png`} alt="" />
                    {getActionCount(gameInstance, myCharacter) > 1 && <span className="pill">{getActionCount(gameInstance, myCharacter)}</span>}
                    <span className="title">Actions</span>
                </div>

                <div className="button" onClick={openInventory} style={{backgroundImage: "url(img/ui/sq_btn_01.png)"}}>
                    <img src={`${baseURL}/img/assets/items/backpack_01.png`} alt="" />
                    <span className="title">Items</span>
                </div>

                <div className="button" onClick={() => { dispatch(openWindow("characterStats")) } } style={{backgroundImage: "url(img/ui/sq_btn_01.png)"}}>
                    <div className="char-thumbnail"
                        style={{
                            position: "absolute",
                            backgroundImage: `url(${baseURL}/${myCharacter?.texture.img})`,
                            width: `${50}px`,
                            height: `${50}px`,
                            borderRadius: `${(50) / 2}px`,
                            top: "5px",
                            left: "5px",
                        }}
                    >
                    </div>
                    {(myCharacter?.exp.pnts ?? 0) > 0 && <span className="pill">{myCharacter?.exp.pnts}</span>}
                    <span className="title">Stats</span>
                </div>




            </div>

            {myCharacter && <Actions character={myCharacter} gameInstance={gameInstance} />}

            {myCharacter && <CharacterStats character={myCharacter} />}

            {myCharacter && <Combat character={myCharacter} />}

            {myCharacter && <Inventory character={myCharacter} />}

            {myCharacter && <Dialogue character={myCharacter} />}

            {myCharacter && <Info info={info} setInfo={setInfo} />}
        </>
    )


}

export default ThreeTestFC;